import { ModalProps, ModalType } from "../../../../contexts/AppContext";
import { apiTemplateCreate, apiUploadDocxTemplate } from "../../../../sdk";
import { defaultTemplate, PdfRequestData, Template } from "../../../../sdkStructs";
import { omit } from "lodash";
import { AlertData } from "../../../Common/AlertManager/AlertManager";

export interface UploadDocxProps {
  token: string;
  setShowModal: (modalType: ModalType, value: ModalProps) => void;
  isTemplateLoading: boolean;

  stateProps: CreateDocxTemplateProps;
}
interface CreateDocxTemplateProps {
  setLoadingFunc: (value: boolean) => void;
  selectTemplateFunc: (value: Template) => void;
  pushAlert: (value: AlertData) => void;
}

export const handleUploadDocxTemplate = ({ token, setShowModal, isTemplateLoading, stateProps }: UploadDocxProps) => {
  if (isTemplateLoading) return;

  // Create a hidden file input element
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  fileInput.style.display = "none";

  // Handle file selection
  fileInput.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      // Extract filename without extension for initial template name
      const fileName = file.name;
      const lastDotIndex = fileName.lastIndexOf(".");
      const nameWithoutExt = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;

      // Ask for a template name
      setShowModal("name", {
        title: "Template Name",
        submit: (name: string) => handleCreateTemplateWithDocx(token, file, name, stateProps),
        close: () => setShowModal("none", undefined),
        initialValue: nameWithoutExt,
      });
    }
  };

  // Trigger the file selection dialog
  document.body.appendChild(fileInput);
  fileInput.click();
  document.body.removeChild(fileInput);
};

// Add DOCX template handling
const handleCreateTemplateWithDocx = async (
  token: string,
  file: File,
  name: string,
  { setLoadingFunc, selectTemplateFunc, pushAlert }: CreateDocxTemplateProps,
) => {
  if (!token) {
    pushAlert({
      title: "Error",
      text: "Authentication error. Please log in again.",
      type: "error",
      closeButton: true,
      time: 5000,
    });
    return;
  }

  setLoadingFunc(true);

  try {
    // First, create a new template
    const requestTemplate: Template = { ...defaultTemplate, TemplateName: name };
    const templateWithoutId = omit(requestTemplate, "ID");
    const response = await apiTemplateCreate(token, templateWithoutId);
    const newTemplate = response.data;

    // Then, upload the DOCX file to that template
    await apiUploadDocxTemplate(token, newTemplate.ID, file);

    // Update template state and load the new template
    selectTemplateFunc(newTemplate);

    pushAlert({
      title: "Success",
      text: "DOCX template created successfully",
      type: "success",
      closeButton: true,
      time: 5000,
    });
  } catch (error) {
    console.error("Failed to upload DOCX template:", error);
    pushAlert({
      title: "Error",
      text: "Failed to upload DOCX template",
      type: "error",
      closeButton: true,
      time: 5000,
    });
  } finally {
    setLoadingFunc(false);
  }
};

export const downloadDocx = async (token: string, requestData: PdfRequestData) => {
  // Get the PDF service URL from environment or use default
  const pdfServiceUrl = import.meta.env.PROD ? import.meta.env.VITE_PDF_URL : "http://localhost:3000";

  // Request DOCX from the PDF microservice
  const response = await fetch(`${pdfServiceUrl}/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });

  if (!response.ok) {
    throw new Error(`Failed to generate DOCX: ${response.statusText}`);
  }

  // Download the file
  const docxBlob = await response.blob();
  const url = URL.createObjectURL(docxBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${requestData.template.TemplateName || "pentra-report"}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
